(() => {
    'use strict';
    const scope = 'private:admission-funding', owners = new Set();
    async function fundingChoices(url, signal) {
        if (url.origin !== location.origin) throw new Error('El buscador debe pertenecer a esta aplicación.');
        let data;
        if (window.ProfintFields?.catalog) {
            data = await window.ProfintFields.catalog(url, {
                scope, version: '1', classification: 'private-reference', ttl: 0,
                timeout: 8000, requestCache: 'no-store', signal
            });
        } else {
            // Progressive enhancement: no second cache when the shared engine is unavailable.
            const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal });
            data = await response.json();
            if (!response.ok) throw new Error(data?.message || 'No pudimos consultar los clientes.');
        }
        if (!Array.isArray(data) || data.length > 30 || data.some(row => !row || row.kind !== url.searchParams.get('kind')
            || typeof row.id !== 'string' || !row.id || typeof row.name !== 'string')) throw new Error('La respuesta del buscador no es válida. Volvé a intentar.');
        return data;
    }
    function escapeHtml(value) {
        return String(value ?? '').replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
    }
    function init(root = document, allowNative = false) {
        for (const picker of root.querySelectorAll('[data-funding-picker]:not([data-ready])')) {
            if (picker.querySelector('[data-funding-combo]') && !window.TomSelect && !allowNative) continue;
            const field = picker.querySelector('[data-funding-value]');
            if (!field) continue;
            picker.dataset.ready = 'true';
            const form = picker.closest('form'), status = picker.querySelector('[data-funding-status]');
            const categories = [...picker.querySelectorAll('[name="FundingCategory"]')];
            const originalDisabled = new Map(categories.map(input => [input, input.disabled]));
            const initialEndpoint = picker.dataset.fundingUrl;
            let endpoint = initialEndpoint, combo = null, controller, sequence = 0, timer, pending = null, disposed = false, syncing = false;
            let state = 'idle', lastQuery = '', resetAllowed = true;
            const category = () => categories.find(x => x.checked)?.value || 'Particular';
            const locked = () => field.matches(':disabled') || field.hasAttribute('readonly') || field.getAttribute('aria-disabled') === 'true' || !!picker.closest('[inert]');
            let wasLocked = locked();
            const laborFundingRestricted = () => ['Examen', 'MedicinaLaboral', 'Accidente'].includes(form?.querySelector('[name="Purpose"]:checked')?.value);
            const listeners = [];
            const listen = (target, name, handler) => { target?.addEventListener(name, handler); listeners.push(() => target?.removeEventListener(name, handler)); };
            const retry = document.createElement('button');
            retry.type = 'button'; retry.className = 'btn btn-sm btn-outline-secondary'; retry.textContent = 'Reintentar búsqueda'; retry.dataset.fundingRetry = ''; retry.hidden = true;
            status?.after(retry);
            let nativeSearch = null;
            const setStatus = text => { if (status) status.textContent = text; };
            function fundingOption(value, text, kind, isActive = true, state = null) {
                return { value, text, kind: kind || value.split('|')[0], state, active: isActive === true || isActive === 'true' || isActive === undefined };
            }
            const fromNative = option => option?.value ? fundingOption(option.value, option.text, option.dataset.kind, option.dataset.active, option.dataset.state) : null;
            const initialSelection = fromNative(field.selectedOptions[0]);
            function selected() { return combo ? combo.options[combo.getValue()] : fromNative(field.selectedOptions[0]); }
            function cancel() {
                ++sequence; clearTimeout(timer); controller?.abort();
                // Every SDK load owns exactly one callback, even when fetch ignores abort.
                const previousSync = syncing; syncing = true;
                pending?.finish(); pending = null;
                if (combo) combo.loadedSearches = {};
                syncing = previousSync;
            }
            function replaceRows(data = [], preserve = true) {
                const previousSync = syncing; syncing = true;
                const retained = preserve ? selected() : null;
                const value = retained?.kind === category() ? retained.value : '';
                const options = data.map(item => fundingOption(`${item.kind}|${item.id}`, item.name, item.kind, item.isActive, item.estado));
                if (value && !options.some(item => item.value === value)) options.unshift(retained);
                if (combo) { if (!value) combo.clear(true); combo.clearOptions(); }
                field.replaceChildren(new Option('Seleccionar financiador', ''));
                for (const item of options) {
                    const option = new Option(item.text, item.value, false, item.value === value);
                    option.dataset.kind = item.kind; option.dataset.active = String(item.active); option.dataset.state = item.state || '';
                    field.add(option);
                    if (combo) {
                        const visual = { ...item, $option: option };
                        if (combo.options[item.value]) combo.updateOption(item.value, visual); else combo.addOption(visual);
                    }
                }
                field.value = value;
                if (combo) { combo.updateOriginalInput({ silent: true }); combo.refreshOptions(false); }
                syncing = previousSync;
            }
            function showSelectedStatus() {
                const option = selected();
                if (option?.kind === 'Empresa' && option.active === false)
                    setStatus('La empresa no está activa. Podés continuar la admisión; Administración podrá corregir su condición después.');
                else if (option?.active === false) setStatus('Este cliente está inactivo. Revisá su condición administrativa.');
                else if (option?.value) setStatus('Financiador seleccionado para esta atención.');
            }
            function checkContext() {
                if (!picker.isConnected) { destroy(); return false; }
                if (endpoint !== picker.dataset.fundingUrl) {
                    endpoint = picker.dataset.fundingUrl; resetAllowed = false; cancel(); replaceRows([], false);
                    lastQuery = ''; nativeSearch && (nativeSearch.value = ''); combo?.setTextboxValue('');
                    state = 'idle'; retry.hidden = true;
                    field.dispatchEvent(new Event('change', { bubbles: true }));
                    setStatus('El contexto cambió. Volvé a buscar el financiador.');
                }
                const disabled = locked();
                if (disabled !== wasLocked) {
                    wasLocked = disabled; cancel(); replaceRows(); retry.hidden = true; state = 'idle';
                    applyFundingRules();
                    if (nativeSearch) nativeSearch.disabled = disabled;
                    if (combo) { if (disabled) combo.lock(); else if (!combo.isDisabled) combo.unlock(); }
                }
                return !disposed && !disabled;
            }
            function load(query, callback = () => {}) {
                let completed = false;
                const finish = () => { if (completed) return; completed = true; const previousSync = syncing; syncing = true; callback(); syncing = previousSync; };
                if (!checkContext() || category() === 'Particular') { finish(); return; }
                const kind = category(), term = String(query || '').trim();
                let url;
                try {
                    url = new URL(endpoint, location.origin); url.searchParams.set('kind', kind); url.searchParams.set('q', term);
                    if (url.origin !== location.origin) throw new Error('El buscador debe pertenecer a esta aplicación.');
                } catch (error) { state = 'error'; setStatus(error.message); finish(); return; }
                if (pending?.url === url.href) { finish(); return; }
                cancel(); replaceRows();
                const current = sequence, context = endpoint;
                controller = new AbortController(); const signal = controller.signal;
                pending = { url: url.href, finish };
                state = 'loading'; retry.hidden = true; lastQuery = term; setStatus('Buscando financiadores...');
                const deadline = setTimeout(() => {
                    if (current !== sequence || disposed) return;
                    state = 'error'; setStatus('La búsqueda demoró demasiado. Volvé a intentar.'); retry.hidden = false;
                    cancel();
                }, 8000);
                fundingChoices(url, signal).then(data => {
                    if (disposed || current !== sequence || context !== picker.dataset.fundingUrl || kind !== category() || locked()) return;
                    replaceRows(data); state = 'ready';
                    setStatus(data.length ? `${data.length} opciones encontradas${data.length === 30 ? ' · refiná la búsqueda para ver más' : ''}.` : 'Sin coincidencias. Probá por código o nombre.');
                }).catch(error => {
                    if (disposed || current !== sequence || context !== picker.dataset.fundingUrl || kind !== category()) return;
                    if (error.name === 'AbortError' && !error.timedOut && signal.aborted) return;
                    state = 'error'; retry.hidden = false;
                    setStatus(error.timedOut ? 'La búsqueda demoró demasiado. Volvé a intentar.' : error.status === 401 || error.status === 403
                        ? 'No tenés acceso a esta búsqueda. Revisá la sesión y volvé a intentar.' : error.name === 'AbortError'
                            ? 'La búsqueda se canceló. Volvé a intentar.' : error.message || 'No pudimos buscar. Volvé a intentar.');
                }).finally(() => {
                    clearTimeout(deadline); finish();
                    if (pending?.finish === finish) pending = null;
                });
            }
            combo = window.TomSelect ? new window.TomSelect(field, {
                allowEmptyOption: true, valueField: 'value', labelField: 'text', searchField: ['text'], maxOptions: 31,
                preload: false, refreshThrottle: 250, loadThrottle: null,
                options: [...field.options].filter(x => x.value).map(fromNative),
                placeholder: 'Buscar por código o nombre...', dropdownParent: picker.closest('dialog') || 'body',
                load(query, callback) { load(query, callback); },
                render: {
                    option(data) {
                        const active = data.active !== false;
                        return `<div class="admission-funding-option ${active ? '' : 'is-inactive'}"><div><strong>${escapeHtml(data.text)}</strong><small>${escapeHtml(data.kind || '')}</small></div>` +
                            `<span class="admission-funding-option__state ${active ? 'is-active' : 'is-inactive'}">${data.state === 'Suspendido' ? 'Suspendido' : active ? 'Activo' : 'Inactivo'}</span></div>`;
                    },
                    item(data) { return `<div>${escapeHtml(data.text)} <span class="admission-funding-option__state ${data.active === false ? 'is-inactive' : 'is-active'}">${data.state === 'Suspendido' ? 'Suspendido' : data.active === false ? 'Inactivo' : 'Activo'}</span></div>`; },
                    no_results() { return `<div class="no-results">${state === 'error' ? 'No se pudo buscar. Usá Reintentar búsqueda.' : state === 'loading' ? 'Buscando financiadores...' : state === 'ready' ? 'Sin resultados' : 'Buscá por código o nombre'}</div>`; }
                },
                onDropdownOpen() { if (!syncing && !pending) combo?.load(combo.control_input.value); },
                onDropdownClose() { if (!syncing) { cancel(); replaceRows(); } },
                onChange() { if (!syncing) showSelectedStatus(); }
            }) : null;
            if (!combo) {
                nativeSearch = document.createElement('input'); nativeSearch.type = 'search'; nativeSearch.className = 'form-control mb-2';
                nativeSearch.setAttribute('aria-label', 'Buscar financiador por código o nombre'); nativeSearch.placeholder = 'Buscar por código o nombre...'; nativeSearch.maxLength = 100;
                field.before(nativeSearch);
                listen(nativeSearch, 'input', () => {
                    cancel(); state = 'loading'; retry.hidden = true; replaceRows(); setStatus('Buscando financiadores...');
                    timer = setTimeout(() => load(nativeSearch.value), 250);
                });
                listen(nativeSearch, 'focus', () => { if (!pending) load(nativeSearch.value); });
                listen(field, 'focus', () => { if (!pending) load(nativeSearch.value); });
            } else {
                combo.control_input.maxLength = 100;
                listen(combo.control_input, 'input', () => { cancel(); state = 'loading'; retry.hidden = true; replaceRows(); setStatus('Buscando financiadores...'); });
            }
            function search(query = '') { if (combo) combo.load(query); else load(query); }
            listen(retry, 'click', () => search(lastQuery));
            function applyFundingRules() {
                const blocked = laborFundingRestricted() ? new Set(['Particular', 'ObraSocial']) : new Set();
                let changed = false;
                for (const input of categories) {
                    const disabled = locked() || originalDisabled.get(input) || blocked.has(input.value);
                    input.disabled = disabled;
                    input.closest('[data-funding-kind]')?.classList.toggle('is-disabled', disabled);
                    input.closest('[data-funding-kind]')?.setAttribute('aria-disabled', disabled ? 'true' : 'false');
                }
                if (blocked.has(category())) {
                    const fallback = categories.find(x => !x.disabled && x.value === 'Art')
                        || categories.find(x => !x.disabled && x.value === 'Empresa')
                        || categories.find(x => !x.disabled && x.value === 'Colegio') || categories.find(x => !x.disabled);
                    if (fallback) { fallback.checked = true; changed = true; }
                }
                return changed;
            }
            function choose(value, name) {
                if (combo) { if (!combo.options[value]) combo.addOption(fundingOption(value, name)); combo.setValue(value, true); }
                else { if (![...field.options].some(option => option.value === value)) field.add(new Option(name, value)); field.value = value; }
                field.dispatchEvent(new Event('change', { bubbles: true }));
            }
            function changeCategory(shouldLoad = true) {
                const ruleChangedCategory = applyFundingRules(), kind = category();
                cancel(); replaceRows(); lastQuery = ''; combo?.setTextboxValue(''); if (nativeSearch) nativeSearch.value = '';
                state = 'idle'; retry.hidden = true;
                picker.querySelector('[data-funding-select-area]').hidden = kind === 'Particular';
                picker.querySelector('[data-new-company]').hidden = kind !== 'Empresa';
                const employment = form?.querySelector('[data-employment-area]');
                if (employment) { employment.hidden = kind !== 'Empresa'; if (kind !== 'Empresa') form.querySelector('[name="EmploymentChange"]').value = 'Keep'; }
                if (kind === 'Particular') { choose('Particular|', 'Particular'); setStatus('El paciente paga esta atención.'); }
                else {
                    field.dispatchEvent(new Event('change', { bubbles: true }));
                    setStatus('Buscá por código o nombre y elegí el financiador de esta atención.');
                    if (shouldLoad || ruleChangedCategory) search();
                }
            }
            categories.forEach(input => listen(input, 'change', () => changeCategory()));
            listen(field, 'change', () => { if (!combo) showSelectedStatus(); });
            form?.querySelectorAll('[name="Purpose"]').forEach(input => listen(input, 'change', () => {
                const wasBlocked = laborFundingRestricted() && ['Particular', 'ObraSocial'].includes(category());
                changeCategory(wasBlocked);
                if (laborFundingRestricted()) setStatus('Las atenciones laborales no se cargan por Particular ni por Obra social. Elegí Empresa, ART, Colegio o el financiador laboral correspondiente.');
            }));
            listen(form, 'reset', () => queueMicrotask(() => {
                if (disposed) return;
                checkContext(); cancel(); replaceRows([], false);
                if (resetAllowed && endpoint === initialEndpoint && initialSelection?.kind === category()) {
                    const item = initialSelection;
                    replaceRows([{ kind: item.kind, id: item.value.split('|')[1], name: item.text, isActive: item.active, estado: item.state }], false);
                    choose(item.value, item.text);
                }
                changeCategory(false); showSelectedStatus();
            }));
            const observer = new MutationObserver(() => checkContext());
            function refresh(clearSelection = false) {
                if (disposed) return;
                if (clearSelection) {
                    resetAllowed = false; lastQuery = '';
                    combo?.setTextboxValue(''); if (nativeSearch) nativeSearch.value = '';
                }
                cancel(); replaceRows([], !clearSelection); retry.hidden = true; state = 'idle';
                if (clearSelection) field.dispatchEvent(new Event('change', { bubbles: true }));
                setStatus(category() === 'Particular' ? 'El paciente paga esta atención.' : 'Volvé a buscar para actualizar las opciones.'); showSelectedStatus();
            }
            function destroy() {
                if (disposed) return;
                disposed = true; cancel(); observer.disconnect(); listeners.forEach(remove => remove()); owners.delete(owner);
                const retained = selected(); combo?.destroy(); combo = null;
                field.replaceChildren(new Option('Seleccionar financiador', ''));
                if (retained?.value) { const option = new Option(retained.text, retained.value, true, true); option.dataset.kind = retained.kind; option.dataset.active = String(retained.active); option.dataset.state = retained.state || ''; field.add(option); }
                nativeSearch?.remove(); retry.remove(); delete picker.dataset.ready;
            }
            const owner = { picker, refresh, destroy }; owners.add(owner);
            changeCategory(false); showSelectedStatus();
            if (wasLocked) { if (combo) combo.lock(); if (nativeSearch) nativeSearch.disabled = true; }
            observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-funding-url', 'disabled', 'readonly', 'inert', 'aria-disabled'] });
        }
        for (const form of root.querySelectorAll('.admission-product form:not([data-summary-ready])')) {
            if (!form.querySelector('[data-summary-funding]')) continue;
            form.dataset.summaryReady = 'true';
            function summary() {
                const set = (selector, value) => { const element = form.querySelector(selector); if (element) element.textContent = value; };
                const funding = form.querySelector('[name="Funding"]');
                set('[data-summary-funding]', funding?.value ? funding.selectedOptions[0]?.text || 'Seleccionar' : 'Elegí un financiador');
                const purpose = form.querySelector('[name="Purpose"]:checked');
                if (purpose) {
                    const exam = purpose.value === 'Examen';
                    const labor = purpose.value === 'MedicinaLaboral';
                    form.querySelector('[data-exam-types]').hidden = !exam;
                    const laborTypes = form.querySelector('[data-labor-types]');
                    if (laborTypes) laborTypes.hidden = !labor;
                    if (exam && !form.querySelector('[data-exam-types] [name="ExaminationType"]:checked')) {
                        const fallback = form.querySelector('[data-exam-types] [name="ExaminationType"]');
                        if (fallback) fallback.checked = true;
                    }
                    if (labor && !form.querySelector('[data-labor-types] [name="ExaminationType"]:checked')) {
                        const fallback = form.querySelector('[data-labor-types] [name="ExaminationType"]');
                        if (fallback) fallback.checked = true;
                    }
                    const examType = labor
                        ? form.querySelector('[data-labor-types] [name="ExaminationType"]:checked')
                        : form.querySelector('[data-exam-types] [name="ExaminationType"]:checked') || form.querySelector('[name="ExaminationType"]:checked') || form.querySelector('[name="ExaminationType"]');
                    const examName = examType?.dataset?.examLabel || examType?.selectedOptions?.[0]?.text || examType?.value;
                    const names = { LicenciaProfesional: 'Licencia profesional de conducir', Examen: examName || 'Examen ocupacional', Enfermedad: 'Otro motivo · Consulta general', MedicinaLaboral: `Laboral · ${examName || 'Médico general'}`, Accidente: 'Accidente', MedicinaLegal: 'Medicina legal' };
                    set('[data-summary-purpose]', names[purpose.value] || 'Otro motivo');
                    set('[data-summary-next]', purpose.value === 'LicenciaProfesional' ? 'En el siguiente paso revisás el protocolo de licencia y sus prácticas.' : exam ? 'En el siguiente paso elegís el módulo y revisás las prácticas y los prestadores.' : labor ? 'En el siguiente paso revisás la consulta laboral y el profesional correspondiente.' : 'En el siguiente paso elegís la consulta y las prácticas necesarias.');
                }
                const arrival = form.querySelector('[name="ArrivalMode"]:checked');
                if (arrival) set('[data-summary-arrival]', { Express: 'Express', Appointment: 'Con turno', WalkIn: 'Demanda espontánea' }[arrival.value]);
                const exam = form.querySelector('[name="ExaminationType"]:checked') || form.querySelector('[name="ExaminationType"]');
                if (exam) set('[data-summary-exam]', exam.dataset?.examLabel || exam.selectedOptions?.[0]?.text || exam.value);
                const present = form.querySelector('[data-patient-present]');
                if (present) form.querySelector('[data-schedule-area]').hidden = present.checked;
                const employment = form.querySelector('[name="EmploymentChange"]');
                if (employment) {
                    const end = form.querySelector('[data-exit-choice]');
                    const isExit = exam?.value === 'Egreso'; end.disabled = !isExit;
                    if (!isExit && employment.value === 'EndAfterVisit') employment.value = 'Keep';
                    set('[data-employment-description]', { Keep: 'El financiador se guarda en esta atención. El historial de las anteriores se conserva.', Replace: 'Al confirmar, se cerrarán los vínculos laborales anteriores y ésta será la empresa actual.', EndAfterVisit: 'La empresa paga este examen. El vínculo termina al completar todas las prácticas obligatorias y registrar la salida; una visita interrumpida conserva el vínculo.' }[employment.value]);
                }
            }
            form.addEventListener('change', event => {
                const employment = form.querySelector('[name="EmploymentChange"]');
                if (employment && event.target.name === 'ExaminationType' && event.target.value === 'Egreso'
                    && form.querySelector('[name="Funding"]').value.startsWith('Empresa|')) employment.value = 'EndAfterVisit';
                summary();
            }); summary();
        }
    }
    init();
    queueMicrotask(() => init(document, true));
    document.addEventListener('DOMContentLoaded', () => init(document, true));
    document.addEventListener('htmx:afterSwap', () => { init(); queueMicrotask(() => init(document, true)); });
    window.addEventListener('profint:controls-ready', () => init());
    document.addEventListener('htmx:beforeCleanupElement', event => {
        const root = event.detail?.elt || event.target;
        for (const owner of [...owners]) if (root === owner.picker || root?.contains?.(owner.picker)) owner.destroy();
    });
    function invalidate(clearSelection = false) {
        window.ProfintFields?.clearCatalogs?.(scope);
        for (const owner of [...owners]) owner.refresh(clearSelection);
    }
    ['focus', 'profint:clients-changed'].forEach(name => window.addEventListener(name, () => invalidate()));
    ['profint:auth-changed', 'profint:tenant-changed', 'profint:permissions-changed'].forEach(name => window.addEventListener(name, () => invalidate(true)));
    window.addEventListener('pagehide', () => invalidate());
    window.addEventListener('pageshow', event => { if (event.persisted) invalidate(); });
})();
